import {BaseElement, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {BarActions} from "/@back@/commons/widgets/bars.js";
import {POPUP, PopupActions} from "/@back@/commons/widgets/popups.js";
import {IWedEditorCommonBar, IWedFocusBarPointer} from "/@back@/edit/wed/features/commonBar.js";
import {IWedEditor, IWedEditorMain, OWedEditorMainConfig, OWedManagerConfig, WedEditorMiniview, WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {ITxtElement, TxtRoot} from "/@back@/edit/wed/wedlets/txt/txt.js";
import {TXTTABLE} from "/@back@/edit/wed/wedlets/txt/txtTable.js";
import {InlElement, InlObject, InlWrap, TxtBlock, TxtCell, TxtCol, TxtElement, TxtObject, TxtRow, TxtStr, TxtTable} from "/@back@/edit/wed/wedlets/txt/txtTags.js";
import {
	buildXa,
	buildXaInVirtual,
	findTxtEltFirstChild,
	findTxtEltLastChild,
	findTxtEltNextSibling,
	findTxtEltOrRootParent,
	findTxtEltParent,
	findTxtEltPreviousSibling,
	findTxtStrFirstChild,
	findTxtStrLastChild,
	findTxtStrNext,
	findTxtStrParent,
	findTxtStrPrevious,
	getTextFirstOffset,
	getTextLength,
	IFixCaret,
	IFixSelection,
	IS_TxtBlock,
	IS_TxtElement,
	IS_TxtRoot,
	IS_TxtStr,
	webRange2XaRange
} from "/@back@/edit/wed/wedlets/txt/txtUtils.js";
import {findScrollCtn, IWedletActionCtx, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {Action} from "/@lib@/commons/actions.js";
import {REG} from "/@lib@/commons/registry.js";
import {DOM, ENodeType, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IXAddr, IXAddrRange, IXAddrRangeSel, XA} from "/@lib@/commons/xml/xAddr.js";
import {IDocHolder} from "/@lib@/edit/docHolder.js";
import {Msg} from "/@lib@/edit/ot/urban.js";
import {isXmlMsg, XmlBatch, XmlDeleteMsg, XmlInsertMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {OSkTableImportContext} from "/@lib@/edit/schema/schemaMetaTxt.js";
import {IPopupCloseEvent} from "/@back@/commons/widgets/popupable.js";
import {IView} from "/@lib@/commons/views.js";
import {GFX} from "/@lib@/commons/utils/gfx.js";
import {HistoEditPoint} from "/@lib@/edit/histoEditPoints.js";
import {CLIPBOARD} from "/@lib@/commons/clipboard.js";
import LogicTable = TXTTABLE.LogicTable;
import IS_text = DOM.IS_text;


export interface OWedEditorTxtConfig extends OWedEditorMainConfig {
	hideStackBar?: boolean;
}

export interface IWedEditorTxtSel extends IWedEditorMain {
	showTxtStackBar(): TxtStackBar

	hideTxtStackBar(): void

	_txtStackBar?: TxtStackBar

	/** Mini rendering en popup d'un fragment du contenu (preview latex...). */
	_renderingEd?: WedEditorMiniview;
}

export function AgTxtSelEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorTxtSel;
	const initFromDocHolder = proto.initFromDocHolder;
	proto.initFromDocHolder = function (this: IWedEditor, docHolder: IDocHolder, config?: OWedManagerConfig): Promise<boolean> {
		const result = initFromDocHolder.call(this, docHolder, config);
		this.wedMgr.listeners.on("hookMsgForNextSel", hookMsgForNextSel);
		this.wedMgr.listeners.on("selection", selectionChange);
		this.wedMgr.listeners.on("getCurrentSel", getCurrentSel, -10);
		this.wedMgr.listeners.on("getFocus", getFocus);
		return result;
	};

	proto.showTxtStackBar = function (): TxtStackBar {
		if (!this.barLayout) return null; //Editor sans gestion de bars.
		if ((this.config as OWedEditorTxtConfig).hideStackBar) return null;
		if (!this._txtStackBar) {
			this._txtStackBar = new TxtStackBar();
		} else if (!DOM.setHidden(this._txtStackBar, false)) return this._txtStackBar; //déjà affichée
		//Affichage
		if (!this.barLayout.showBar(this._txtStackBar)) return null; //injecte la barre par le layout ou informe de son (ré-affichage).
		const eltFocus = findTxtEltOrRootParent(DOMSH.findDeepActiveElement(), true);
		this._txtStackBar.refreshBar(this.wedMgr, eltFocus ? eltFocus.txtRoot : null);
		return this._txtStackBar;
	};

	proto.hideTxtStackBar = function () {
		if (this._txtStackBar) {
			if (DOM.setHidden(this._txtStackBar, true)) {
				this._txtStackBar.refreshBar(this.wedMgr); //clear
				this.barLayout.onHideBar(this._txtStackBar); //informe le layout
			}
		}
	};
	return cls;
}

function hookMsgForNextSel(wedMgr: WedMgr, transac: Msg, msg: Msg | null): void | true {
	if (transac.getMeta('noFocus') === true || !msg) return;
	let done: void | true;
	//Ce msg devrait provoquer le focus.
	if (wedMgr.docHolderAsync.isMsgFromUs(transac)) {
		//On est à l'origine de ce msg, on prend le Focus.
		if (msg instanceof XmlBatch && msg.selAfter) {
			//batch avec selAfter renseigné.
			const find = {lastAncestorIfNone: true, wedletNotFoundDepth: -1};
			const wedlet = WEDLET.findWedlet(wedMgr.rootWedlet, msg.selAfter.addr, find);
			if (msg.getMeta(TxtSelMgr.MSGMETA_selectTag)) {
				if (IS_TxtElement(wedlet)) {
					done = true;
					wedlet.txtRoot.selMgrAsIs.select(wedlet);
					wedMgr.config.histoEditPointsMgr?.addEditEntry(new HistoEditPoint(wedlet.wedAnchor, wedMgr.docHolder.house), null, wedMgr.config.histoEditHolder);
				}
			} else if (msg.getMeta(TxtSelMgr.MSGMETA_tblLayout)) {
				if (wedlet instanceof TxtCell) {
					const txtTable = DOM.findParent(wedlet, wedMgr.wedEditor.rootNode, TXTTABLE.IS_TxtTable);
					const focusWedlet = msg.selAfter.end ? WEDLET.findWedlet(wedMgr.rootWedlet, msg.selAfter.end, find) : null;
					const focusCell = focusWedlet instanceof TxtCell ? focusWedlet : wedlet;
					txtTable.tableLayout.drawTableLayout(focusCell, wedlet);
				} else if (wedlet instanceof TxtRow) {
					const txtTable = DOM.findParent(wedlet, wedMgr.wedEditor.rootNode, TXTTABLE.IS_TxtTable);
					const firstCell = DOM.findFirstChild(wedlet, TXTTABLE.IS_TxtCell);
					const focusWedlet = msg.selAfter.end ? WEDLET.findWedlet(wedMgr.rootWedlet, msg.selAfter.end, find) : null;
					const focusCell = focusWedlet instanceof TxtRow ? DOM.findLastChild(focusWedlet, TXTTABLE.IS_TxtCell) : DOM.findLastChild(wedlet, TXTTABLE.IS_TxtCell);
					txtTable.tableLayout.drawTableLayout(focusCell, firstCell);
				} else if (wedlet instanceof TxtCol) {
					const txtTable = DOM.findParent(wedlet, wedMgr.wedEditor.rootNode, TXTTABLE.IS_TxtTable);
					const logicTable = txtTable.tableLayout.logicTable;
					let endWedlet = msg.selAfter.end ? WEDLET.findWedlet(wedMgr.rootWedlet, msg.selAfter.end, find) : null;
					if (!(endWedlet instanceof TxtCol)) endWedlet = wedlet;
					txtTable.tableLayout.drawTableLayout(logicTable.getTxtCellAt(0, wedlet.getLogicOffset()), logicTable.getTxtCellAt(logicTable.logicRows.length - 1, (endWedlet as TxtCol).getLogicOffset()));
				} else if (wedlet instanceof TxtTable) {
					const logicTable = wedlet.tableLayout.logicTable;
					wedlet.tableLayout.drawTableLayout(logicTable.getTxtCellAt(0, 0), logicTable.getTxtCellAt(logicTable.logicRows.length - 1, wedlet.countCols() - 1));
				}
				wedMgr.config.histoEditPointsMgr?.addEditEntry(new HistoEditPoint(wedlet.wedAnchor, wedMgr.docHolder.house), null, wedMgr.config.histoEditHolder);
				done = true;
			} else {
				if (IS_TxtElement(wedlet)) {
					done = true;
					const txtRoot = wedlet.txtRoot;
					const subPathFrom = find.wedletNotFoundDepth > 0 ? msg.selAfter.addr.slice(find.wedletNotFoundDepth) : null;
					if (msg.selAfter.end != null) {
						let wedletEnd: TxtElement | TxtRoot;
						let subPathEnd: IXAddr;
						find.wedletNotFoundDepth = -1;
						const end = WEDLET.findWedlet(wedMgr.rootWedlet, msg.selAfter.end, find);
						if (IS_TxtElement(end) && txtRoot === end.txtRoot) {
							wedletEnd = end;
							subPathEnd = find.wedletNotFoundDepth > 0 ? msg.selAfter.end.slice(find.wedletNotFoundDepth) : null;
						} else if (end === txtRoot.wedlet) {
							wedletEnd = txtRoot;
							subPathEnd = find.wedletNotFoundDepth > 0 ? msg.selAfter.end.slice(find.wedletNotFoundDepth) : null;
						} else {
							// exemple: selAfter = fin d'un field TxtRootStr
							wedletEnd = wedlet;
							subPathEnd = subPathFrom;
						}
						if (wedMgr.isUndoRedoPending(transac)) {
							txtRoot.selMgrAsIs.setSelection(wedlet, subPathFrom, wedletEnd, subPathEnd);
						} else {
							txtRoot.selMgrAsIs.setSelection(wedletEnd, subPathEnd);
						}
					} else if (wedlet instanceof InlObject || wedlet instanceof TxtObject) {
						txtRoot.selMgrAsIs.select(wedlet);
					} else {
						txtRoot.selMgrAsIs.setSelection(wedlet, subPathFrom);
					}
					if (wedMgr.config.histoEditPointsMgr) {
						const xa = wedlet.wedAnchor;
						let depth = xa.length;
						for (let p: Node = wedlet; p !== txtRoot; p = p.parentNode) depth--;
						wedMgr.config.histoEditPointsMgr.addEditEntry(new HistoEditPoint(xa, wedMgr.docHolder.house), depth, wedMgr.config.histoEditHolder);
					}
				}
			}
		} else if (isXmlMsg(msg)) {
			const find = {lastAncestorIfNone: true, wedletNotFoundDepth: -1};
			const wedlet = WEDLET.findWedlet(wedMgr.rootWedlet, msg.xa, find);
			if (IS_TxtElement(wedlet)) {
				done = true;
				const txtRoot = wedlet.txtRoot;
				const selMgr = txtRoot.selMgrAsIs;
				try {
					if (txtRoot.webUpdateDone) return;
					let tailXa;
					if (find.wedletNotFoundDepth > 0) {
						//on n'a pas trouvé le XA complet du msg (suppr ou modif dans metas)
						tailXa = msg.xa.slice(find.wedletNotFoundDepth);
						if (msg instanceof XmlInsertMsg && tailXa.length === 1) {
							//Si insertion en fils direct du wedlet
							//TODO différencier le comportement si insertion des metas : tailXa[0]===0 et wedlet.hasMetas() ?
							if (wedMgr.isUndoRedoPending(transac)) {
								//sel de l'insertion en cas de undo/redo
								selMgr.setSelection(wedlet, tailXa, wedlet, XA.incrAtDepth(tailXa.concat(), -1, msg.len));
							} else {
								//sinon on se place après l'insertion.
								selMgr.setSelection(wedlet, XA.incrAtDepth(tailXa, -1, msg.len));
							}
							selMgr.ensureSelVisible();
							return;
						}
						selMgr.setSelection(wedlet, tailXa);
						selMgr.ensureSelVisible();
					} else {
						//on a trouvé le wedlet
						if (msg instanceof XmlDeleteMsg) {
							//suppr=> wedlet est le frère suivant
							selMgr.setCaretBefore(wedlet);
						} else if (msg instanceof XmlInsertMsg && msg.len > 1) {
							if (wedMgr.isUndoRedoPending(transac)) {
								const end = wedlet.wedParent.findWedletChild(XA.last(msg.xa) as number + msg.len - 1);
								selMgr.setSelection(wedlet, null, end instanceof TxtElement ? end : null);
							} else if (selMgr.focusObject instanceof TxtTable) {
								//no-op, on garde le tableLayout actif.
							} else if (transac.getMeta(TxtSelMgr.MSGMETA_caretAtStart)) {
								selMgr.setCaretAtStart(wedlet);
							} else {
								const end = wedlet.wedParent.findWedletChild(XA.last(msg.xa) as number + msg.len - 1);
								if (end instanceof TxtElement) selMgr.setCaretAtEnd(end);
								else selMgr.setCaretAtEnd(wedlet);
							}
						} else {
							if (wedMgr.isUndoRedoPending(transac)) {
								selMgr.select(wedlet);
							} else if (selMgr.focusObject instanceof TxtTable) {
								//no-op, on garde le tableLayout actif.
							} else if (transac.getMeta(TxtSelMgr.MSGMETA_caretAtStart)) {
								selMgr.setCaretAtStart(wedlet);
							} else {
								selMgr.setCaretAtEnd(wedlet);
							}
						}
						selMgr.ensureSelVisible();
					}
				} finally {
					if (selMgr.focusNode) wedMgr.config.histoEditPointsMgr?.addEditEntry(new HistoEditPoint(buildXa(selMgr.focusNode, selMgr.focusOffset), wedMgr.docHolder.house), txtRoot.wedlet.wedAnchor.length, wedMgr.config.histoEditHolder);
				}
			}
		}
	} else {
		//todo montrer les modifs issues des autres houses?
	}
	return done;
}

function selectionChange(wedMgr: WedMgr, sel: Selection | null): void | true {
	let txtRoot: TxtRoot;
	let wedletWithRendering: TxtElement | TxtCell;
	const oldTags = tagsInSel;
	if (sel) {
		let n = sel.focusNode;
		if (n && IS_text(n)) n = n.parentElement;
		while (n) {
			if (IS_TxtRoot(n)) {
				txtRoot = n;
				break;
			}
			const name = n.localName;
			if (name !== "table" && name !== "mark") {
				if (!IS_TxtElement(n)) break;
				if (n.model.renderingModel && !wedletWithRendering) wedletWithRendering = n;
			}
			n = n.parentElement;
		}
		if (txtRoot) {
			txtRoot.selMgrAsIs.onSelChange();
			//StackBar
			(wedMgr.wedEditor as IWedEditorTxtSel)._txtStackBar?.refreshBar(wedMgr, txtRoot);
		}
	}
	if (oldTags) for (const t of oldTags.keys()) t.removeAttribute("in-sel");


	//Rendering
	if (wedletWithRendering) {
		buildRendering(txtRoot, wedletWithRendering);
	} else {
		const renderingEd = (wedMgr.wedEditor as IWedEditorTxtSel)._renderingEd;
		if (renderingEd) POPUP.findPopupableParent(renderingEd).close();
	}
}

let tagsInSel: Set<TxtElement> | null

function getCurrentSel(wedMgr: WedMgr): IXAddrRangeSel | undefined {
	const focus = wedMgr.lastFocus;
	if (!focus) return undefined;
	const txtElt = findTxtEltOrRootParent(focus, true);
	if (!txtElt) return undefined;
	const sel = txtElt.txtRoot.selMgr.getXaRange(false) as IXAddrRangeSel;
	if (!sel) return undefined;
	sel.startInText = true;
	sel.endInText = true;
	return sel;
}

function getFocus(wedMgr: WedMgr, focused: HTMLElement) {
	//StackBar
	const stackBar = (wedMgr.wedEditor as IWedEditorTxtSel)._txtStackBar;
	if (stackBar) {
		if (focused instanceof TxtElement) {
			stackBar.refreshBar(wedMgr, focused.txtRoot);
		} else {
			stackBar.refreshBar(wedMgr);
		}
	}

	//Rendering
	if (IS_TxtElement(focused) && focused.model.renderingModel) {
		buildRendering(DOM.findParent(focused, null, IS_TxtRoot), focused);
	} else {
		const renderingEd = (wedMgr.wedEditor as IWedEditorTxtSel)._renderingEd;
		if (renderingEd) POPUP.findPopupableParent(renderingEd).close();
	}
}

function buildRendering(txtRoot: TxtRoot, wedletWithRendering: TxtElement | TxtCell) {
	let renderingEd = (txtRoot.wedMgr.wedEditor as IWedEditorTxtSel)._renderingEd;
	const wedMgr = txtRoot.wedMgr;
	const xaRoot = wedletWithRendering.wedAnchor;
	if (renderingEd) {
		if (XA.isEquals(renderingEd.wedMgr.rootWedlet.wedAnchor, xaRoot)) return;
		POPUP.findPopupableParent(renderingEd).close(); //Autre point d'ancrage, on va ouvrir une nouvelle popup
	}
	renderingEd = (wedMgr.wedEditor as IWedEditorTxtSel)._renderingEd = new WedEditorMiniview().initialize({reg: wedMgr.reg});
	renderingEd.setWedModel(wedMgr.wedModel);
	(renderingEd as IView).onViewHidden = function () {
		renderingEd.wedMgr.detachDocHolder();
		(wedMgr.wedEditor as IWedEditorTxtSel)._renderingEd = null;
	};
	const config = Object.create(wedMgr.config) as OWedManagerConfig;
	config.xaRoot = xaRoot;
	config.rootModel = wedletWithRendering.model.renderingModel;

	renderingEd.initFromDocHolder(wedMgr.docHolderAsync.cloneDocHolder(), config).then(() => {
		const from = DOM.findParentOrSelf(wedletWithRendering, txtRoot, IS_TxtBlock);
		POPUP.showFloating(renderingEd, {
			fixSize: false,
			initMaxHeight: '10vh',
			posFrom: from,
			targetX: 'end',
			notAvailableSpace: {
				posFrom: from,
				fromX: 'end',
				notAvailableSpace: {
					posFrom: from,
					fromY: 'bottom',
					notAvailableSpace: {
						posFrom: from,
						targetY: 'top'
					}
				}
			}
		}, txtRoot, {titleBar: {closeButton: {}}, resizer: {}});
	});
}

export class TxtSelMgr {

	/**
	 * Key de la meta booléenne d'un batch pour indiquer qu'on devrait afficher la table en mode "layout"
	 * et que les ranges selBefore et selAfter du batch devraient être interprétés comme une sélection
	 * rectangulaire de cellules (ou de lignes ou de colonnes).
	 * Commence par un $ pour indiquer que cette propriété est "stable" (doit être copiée pour les msg undo/redo).
	 */
	static MSGMETA_tblLayout = "$tblLayout";

	/**
	 * Key de la meta booléenne d'un batch pour indiquer de sélectionner le tag inséré via TxtSelMgr.select()
	 * (permet une activation correcte de metas).
	 * Note : pas de préfixe $ : au undo/redo on reselectionne le tout.
	 */
	static MSGMETA_selectTag = "selectTag";

	/**
	 * Key de la meta booléenne d'un batch pour indiquer de placer le cursor au début du contenu modifié.
	 * Note : pas de préfixe $ : au redo, on préfère sélectionner le range complet pour mieux voir la zone éditée.
	 */
	static MSGMETA_caretAtStart = "caretAtStart"

	protected _root: TxtRoot;
	protected _shadowRoot: ShadowRoot | Document;
	_sel: Selection;


	/** TableLayout actuellment actif.  */
	tableLayout: TableLayout;

	/**
	 * Mémoire du dennier objet focusé dans le content de l'éditeur.
	 * Permet de s'affranchir de la perte de focus lié à l'activation des boutons d'actions et menus.
	 */
	protected _lastObject: HTMLElement;

	/**
	 * Mémoire de la précédente sélection(stocké dans un range pour bénéficier
	 * de l'ajustement automatique de l'offset en cas de modif du dom).
	 */
	protected _oldSel = new Range();
	protected _oldFocusIsStart: boolean;

	/**
	 * Mémoire que la sel courante est issue d'un selectAround() et qu'il faut considérer ce noeud
	 * sélectionné dans son intégralité et non son contenu pour les actions de copier/couper/suppr...
	 */
	protected _around: TxtElement;

	protected _updtFromUs: number = 0;

	// get _around() {return this.__around};
	//
	// set _around(elt: TxtElement) {
	// 	if (this.__around !== elt) {
	// 		console.trace("set _around:::", this.__around, elt);
	// 		this.__around = elt;
	// 	}
	// }

	constructor(root: TxtRoot) {
		this._root = root;
		this._shadowRoot = DOMSH.findDocumentOrShadowRoot(root);
		this._sel = this._shadowRoot.getSelection();
	}

	get type(): 'None' | 'Caret' | 'Range' | 'Object' {
		const type = this._sel.type as 'None' | 'Caret' | 'Range';
		return type === 'None' && this._lastObject != null && this._lastObject.isConnected ? 'Object' : type;
	}

	/** Utilisable quelquesoit le type (null si type == 'None'). */
	get focusNode(): HTMLElement | Text | null {return (this._sel.focusNode as HTMLElement | Text) || this._lastObject}

	/** Utilisable quelquesoit le type, mais est != null que si type == 'Object'. */
	get focusObject(): HTMLElement {return this._sel.focusNode !== null ? null : this._lastObject}

	/** Utilisable quelquesoit le type (null si type == 'None'). */
	get commonAncestor(): HTMLElement | Text | null {
		if (this._sel.type === 'Range') return this.range.commonAncestorContainer as HTMLElement | Text;
		return this.focusNode;
	}

	/**
	 * Version plus élaborée que commonAncestor qui retourne le TxtElement si la sel n'enveloppe QUE cet élement,
	 * ie que la sel est en bordure d'un tag inline. Très pratique sur une sélection par drag en partant de l'exétrieur du §.
	 * Utilisable quelquesoit le type (null si type == 'None').
	 * @return null si type == 'None' ou si aucun TxtElement fils de la textPrim n'inclut la sél
	 */
	get commonTxtElement(): TxtElement | null {
		if (this._sel.type !== 'Range') return findTxtEltParent(this.focusNode, true);
		const rg = this.range;
		const parentAnc = this.range.commonAncestorContainer as HTMLElement | Text;
		const startTxtStr = findTxtStrParent(rg.startContainer);
		let targetReg: Range = null;
		if (startTxtStr && parentAnc === startTxtStr.parentElement && startTxtStr.isXmlEndOffset(rg.startContainer, rg.startOffset)) {
			const next = findTxtEltNextSibling(startTxtStr);
			if (next) {
				targetReg = rg.cloneRange();
				targetReg.setStart(next, 0);
			}
		}
		const endTxtStr = findTxtStrParent(rg.endContainer);
		if (endTxtStr && parentAnc === endTxtStr.parentElement && endTxtStr.isXmlStartOffset(rg.endContainer, rg.endOffset)) {
			const prev = findTxtEltPreviousSibling(endTxtStr);
			if (prev) {
				if (!targetReg) targetReg = rg.cloneRange();
				targetReg.setEnd(prev, 0);
			}
		}
		return findTxtEltParent(targetReg ? targetReg.commonAncestorContainer : parentAnc, true);
	}


	/** Utilisable si la sel est Range. */
	get focusOffset(): number {return this._sel.focusOffset}

	/** Utilisable si la sel est Range. */
	get anchorNode(): HTMLElement | Text {return this._sel.anchorNode as HTMLElement | Text}

	/** Utilisable si la sel est Range. */
	get anchorOffset(): number {return this._sel.anchorOffset}

	/** Utilisable si la sel est Range. */
	get range(): Range {return this._sel.getRangeAt(0)}

	/** Retourne non null uniquement si la dernière de sel est issu d'un selectAround(). */
	get selAround(): TxtElement {return this._around}

	/** Retourne un IXAddrRange de la source éditée correspondant à la sel courante.  */
	getXaRange(inVirtual?: boolean): IXAddrRange | null {
		switch (this.type) {
		case 'Range':
			if (this._around) return XA.newRangeAround(this._around.wedAnchor);
			return webRange2XaRange(this.range);
		case 'Caret':
			return {start: inVirtual ? buildXaInVirtual(this.focusNode, this.focusOffset) : buildXa(this.focusNode, this.focusOffset)};
		case 'Object':
			const elt = findTxtEltParent(this._lastObject, true);
			return elt ? XA.newRangeAround(elt.wedAnchor) : null;
		}
		return null;
	}

	/** Retourne un IXAddrRange dont les extrémitrés sont dans les blocs (donc range de la sel, pas de _around).  */
	getXaRangeInBlock(inVirtual?: boolean): IXAddrRange {
		switch (this.type) {
		case 'Range':
			return webRange2XaRange(this.range);
		case 'Caret':
			return {start: inVirtual ? buildXaInVirtual(this.focusNode, this.focusOffset) : buildXa(this.focusNode, this.focusOffset)};
		case 'Object':
			const elt = findTxtEltParent(this._lastObject, true);
			if (!elt) return null;
			const xa = XA.freeze(elt.wedAnchor);
			return {start: xa, end: XA.incrAtDepth(xa, -1, 1)};
		}
		return null;
	}

	/**
	 * Parcours dans l'ordre naturel des noeuds tous les noeuds intégralement contenus dans la sélection.
	 * Attention : pour le 1er et le dernier noeud, si c'est un noeud texte, il est retourné même si tout le texte n'est pas sélectionné.
	 * Deux noeuds ancêtres l'un de l'autre ne sont jamais retournés, c'est toujours le noeud le plus 'haut' intégralement inclus
	 * dans la sel qui est retourné.
	 */
	iterateNodesInSel(cb: (node: Node) => void) {
		if (this._around) {
			cb(this._around);
			return;
		}
		const range = this.range;
		const ancestor = range.commonAncestorContainer;
		let node = range.startContainer;
		if (node.nodeType === ENodeType.element) node = node.childNodes.item(range.startOffset) || node;
		cb(node);
		if (node === ancestor) return;
		while (node.parentNode !== ancestor) {
			let sib = node.nextSibling;
			while (sib) {
				cb(sib);
				sib = sib.nextSibling;
			}
			node = node.parentNode;
		}
		//à ce stade : node.parentNode === ancestor
		let endNode = range.endContainer;
		if (endNode.nodeType === ENodeType.element) endNode = endNode.childNodes.item(range.endOffset) || endNode;
		let endChain;
		while (endNode.parentNode !== ancestor) {
			if (!endChain) endChain = [endNode];
			else endChain.push(endNode);
			endNode = endNode.parentNode;
		}
		//à ce stade : endNode.parentNode === ancestor
		node = node.nextSibling;
		while (node !== endNode) {
			cb(node);
			node = node.nextSibling;
		}
		if (endChain) {
			for (let i = endChain.length - 1; i >= 0; i--) {
				const stop = endChain[i];
				let sib = stop.parentNode.firstChild;
				while (sib !== stop) {
					cb(sib);
					sib = sib.nextSibling;
				}
			}
		}
		cb(endChain ? endChain[0] : endNode);
	}

	getSelRect(): DOMRect | null {
		switch (this.type) {
		case 'Range':
			if (this._around) return this._around.getBoundingClientRect();
			return this.range.getBoundingClientRect();
		case 'Caret':
			return this.range.getBoundingClientRect();
		case 'Object':
			return this._lastObject.getBoundingClientRect();
		}
		return null;
	}

	/**
	 *
	 */
	ensureSelVisible() {
		const node = this.focusNode;
		if (node) BASIS.ensureVisible(DOM.findParentOrSelf(node, null, DOM.IS_element) as HTMLElement, findScrollCtn(node), "nearest");
	}

	/**
	 * Assure que la sélection courante soit au centre de la zone de scroll.
	 * A utiliser pour link externes (annots...).
	 */
	ensureSelCentered(behavior?: 'smooth' | 'instant') {
		const node = this.focusNode;
		const ctn = findScrollCtn(node);//A tester : || document.body ou || document.documentElement;
		if (ctn) {
			const selRect = this.getSelRect();
			if (selRect) {
				const scrollRect = ctn.getBoundingClientRect();
				const delatY = selRect.top - ((scrollRect.top + scrollRect.bottom) / 2);
				if (delatY > scrollRect.height * 2) behavior = 'instant';
				ctn.scrollBy({top: delatY, behavior: behavior || 'smooth'} as any);
			}
		}
	}

	/** A appeler par les actions et affichages de menus pour restaurer la sélection initiale. */
	restoreSel(): TxtSelMgr {
		this._root.unfreezeSel();
		//on a la mémoire d'un dernier focus et pas de sel courante.
		if (this._lastObject != null && !this._sel.focusNode) this._lastObject.focus();
		else {
			this._root.focus();
			if (this._oldSel.startContainer) {
				if (this._oldFocusIsStart) {
					this._sel.setBaseAndExtent(this._oldSel.endContainer, this._oldSel.endOffset, this._oldSel.startContainer, this._oldSel.startOffset);
				} else {
					this._sel.setBaseAndExtent(this._oldSel.startContainer, this._oldSel.startOffset, this._oldSel.endContainer, this._oldSel.endOffset);
				}
				this.fixupSel(); //le _oldSel est parfois mém
			}
		}
		return this;
	}

	collapse(parentNode: Node, offset: number) {
		this._updtFromUs++;
		this._sel.collapse(parentNode, offset);
		this.fixupSel();
	}

	collapseToStart() {
		this._updtFromUs++;
		if (this._sel.focusNode) this._sel.collapseToStart();
		this.fixupSel(); //si la sel est hors des noeuds textes (ctrl+A)...
	}

	collapseToEnd() {
		this._updtFromUs++;
		if (this._sel.focusNode) this._sel.collapseToEnd();
		this.fixupSel(); //si la sel est hors des noeuds textes (ctrl+A)...
	}

	modify(alter: 'move' | 'extend', direction: 'forward' | 'backward', granularity: 'character' | 'word') {
		this._updtFromUs++;
		//FIXME non standard (impl firefox & chrome) mais permet de gérer les caracères codePoints...
		(this._sel as any).modify(alter, direction, granularity);
	}

	trim() {
		if (this._sel.type === 'Range') {
			this.fixupSel();
			const rg = this.range;
			const start = rg.startContainer;
			let startOffset = rg.startOffset;
			const end = rg.endContainer;
			let endOffset = rg.endOffset;
			if (start === end) {
				if (start.nodeType === ENodeType.text) {
					while (startOffset < endOffset && /\s/.test(start.nodeValue.charAt(startOffset))) startOffset++;
					while (startOffset < endOffset && /\s/.test(end.nodeValue.charAt(endOffset - 1))) endOffset--;
				}
			} else {
				if (start instanceof Text) {
					const len = getTextLength(start);
					while (startOffset < len && /\s/.test(start.nodeValue.charAt(startOffset))) startOffset++;
				}
				if (end instanceof Text) {
					const startO = getTextFirstOffset(end);
					while (endOffset > startO && /\s/.test(end.nodeValue.charAt(endOffset - 1))) endOffset--;
				}
			}
			if (startOffset !== rg.startOffset || endOffset !== rg.endOffset) {
				if (start === this._sel.focusNode && rg.startOffset === this._sel.focusOffset) {
					this._sel.setBaseAndExtent(end, endOffset, start, startOffset);
				} else {
					this._sel.setBaseAndExtent(start, startOffset, end, endOffset);
				}
				this.memSel(this.range);
			}
		}
	}

	resetSel() {
		this._sel.removeAllRanges();
	}

	select(elt: TxtElement) {
		this._updtFromUs++;
		let start = elt.findOrCreateTxtStrOrObject(false);
		let end = elt.findOrCreateTxtStrOrObject(true);
		if (start === end) {
			if (start instanceof TxtStr) {
				const [startNode, startOffset] = start.getWebOffset(0);
				const [endNode, endOffset] = start.getWebOffset(start.getXmlTextLength());
				this._sel.setBaseAndExtent(startNode, startOffset, endNode, endOffset);
			} else {
				start.focus(); // sel d'objet.
			}
		} else {
			if (!(start instanceof TxtStr)) start = findTxtStrPrevious(elt);
			if (!(end instanceof TxtStr)) end = findTxtStrNext(elt);
			const [startNode, startOffset] = (start as TxtStr).getWebOffset(0);
			const [endNode, endOffset] = (end as TxtStr).getWebOffset((end as TxtStr).getXmlTextLength());
			this._sel.setBaseAndExtent(startNode, startOffset, endNode, endOffset);
		}
	}

	selectAround(elt: TxtElement) {
		if (elt instanceof InlElement) {
			const prevTxt = elt.findOrCreateCaretableSibling(true);
			const nextTxt = elt.findOrCreateCaretableSibling(false);
			const [startNode, startOffset] = prevTxt.getWebOffset(prevTxt.getXmlTextLength());
			const [endNode, endOffset] = nextTxt.getWebOffset(0);
			this._sel.setBaseAndExtent(startNode, startOffset, endNode, endOffset);
			this._around = elt;
		} else {
			this.select(elt);
			this._around = elt;
		}
	}

	selectAll() {
		this._updtFromUs++;
		const start = findTxtStrFirstChild(this._root);
		const end = findTxtStrLastChild(this._root);
		const [startNode, startOffset] = (start as TxtStr).getWebOffset(0);
		const [endNode, endOffset] = (end as TxtStr).getWebOffset((end as TxtStr).getXmlTextLength());
		this._sel.setBaseAndExtent(startNode, startOffset, endNode, endOffset);
	}

	setCaretAtEnd(elt: TxtElement) {
		this._updtFromUs++;
		let end = elt.findOrCreateTxtStrOrObject(true);
		if (!(end instanceof TxtStr)) end = findTxtStrNext(elt);
		const [endNode, endOffset] = (end as TxtStr).getWebOffset((end as TxtStr).getXmlTextLength());
		this._sel.collapse(endNode, endOffset);
	}

	setCaretAtStart(elt: TxtElement) {
		this._updtFromUs++;
		const first = elt instanceof TxtStr ? elt : findTxtStrNext(elt);
		if (!first) return;
		const [endNode, endOffset] = first.getWebOffset(0);
		this._sel.collapse(endNode, endOffset);
	}

	setCaretBefore(elt: TxtElement) {
		this._updtFromUs++;
		const str = findTxtStrPrevious(elt);
		if (str) {
			const [startNode, startOffset] = str.getWebOffset(str.getXmlTextLength());
			this._sel.collapse(startNode, startOffset);
		} else {
			this.setCaretAtStart(elt);
		}
	}


	setSelection(from: ITxtElement, subPathFrom?: IXAddr, to?: ITxtElement, subPathTo?: IXAddr) {
		this._updtFromUs++;
		//console.log("setSelection::::", from, subPathFrom, to, subPathTo);
		if (!to) {
			//caret
			if (subPathFrom != null) {
				if (from instanceof TxtStr) {
					const [node, offset] = from.getWebOffset(subPathFrom[0] as number);
					this.collapse(node, offset);
					return;
				} else {
					//édition dans des metas ou point après le dernier élément, donc sel par la fin.
					if (IS_TxtElement(from)) from.setCaretIn(this, false);
					else {
						//on est sur txtRoot.
						const last = findTxtEltLastChild(from);
						if (last) last.setCaretIn(this, false);
						else from.focus();
					}
					return;
				}
			}
			if (IS_TxtElement(from)) from.setCaretIn(this, true);
			else if (from) from.focus(); //on est sur TxtRoot.
		} else {
			//sélection
			let startNode = from as Node;
			let startOffset = 0;
			let endNode = to as Node;
			let endOffset = 0;
			if (subPathFrom != null) {
				const offs = subPathFrom[0];
				if (typeof offs === 'number') {
					if (from instanceof TxtStr) {
						[startNode, startOffset] = from.getWebOffset(offs);
					} else if (offs > 0) {
						//start après le denier fils
						const str = DOM.findPreviousIn(from, IS_TxtStr) || findTxtStrPrevious(from);
						[startNode, startOffset] = str.getWebOffset(str.getXmlTextLength());
					}
				}
			}
			if (subPathTo != null) {
				const offs = subPathTo[0];
				if (typeof offs === 'number') {
					if (to instanceof TxtStr) {
						[endNode, endOffset] = to.getWebOffset(offs);
					} else if (offs > 0) {
						//end après le denier fils
						const str = DOM.findPreviousIn(to, IS_TxtStr) || findTxtStrNext(to);
						[endNode, endOffset] = str.getWebOffset(str.getXmlTextLength());
					}
				}
			}
			this._sel.setBaseAndExtent(startNode, startOffset, endNode, endOffset);
			this.fixupSel();
		}
	}

	setSelFocus(focusNode: Text, offset: number) {
		this._updtFromUs++;
		if (this._sel.anchorNode) this._sel.extend(focusNode, offset);
		else this._sel.setBaseAndExtent(focusNode, offset, focusNode, offset);
	}

	setSelAnchor(anchorNode: Text, offset: number) {
		this._updtFromUs++;
		this._sel.setBaseAndExtent(anchorNode, offset, this._sel.focusNode, this._sel.focusOffset);
	}

	setSelCaret(node: Text, anchorOffset: number) {
		this._updtFromUs++;
		this._sel.setBaseAndExtent(node, anchorOffset, node, anchorOffset);
	}

	setSelBounds(anchorNode: Text, anchorOffset: number, focusNode: Text, focusOffset: number) {
		this._updtFromUs++;
		this._sel.setBaseAndExtent(anchorNode, anchorOffset, focusNode, focusOffset);
	}

	protected _selFixing: boolean;

	fixupSel(): boolean {
		if (this._selFixing) return false;
		this._selFixing = true;
		try {
			const focusNode = this._sel.focusNode;
			if (!focusNode) return;
			const around = this._around;
			const anchorNode = this._sel.anchorNode;
			//console.log("Before fixupSel::", this._sel.type, this._sel.focusNode, this._sel.focusOffset, this._sel.anchorNode, this._sel.anchorOffset);
			let updated = false;
			const parent = DOMSH.getFlatParentElt(this._root);
			const oldFocus = this._oldFocusIsStart ? this._oldSel.startContainer : this._oldSel.endContainer;
			const oldFocusOffs = this._oldFocusIsStart ? this._oldSel.startOffset : this._oldSel.endOffset;
			if (focusNode === anchorNode) {
				//console.log("selcollpased!!!", sel.anchorNode, sel.anchorOffset, sel.focusNode, sel.focusOffset);
				if (this._sel.anchorOffset === this._sel.focusOffset) {
					DOMSH.findFlatParentElt(focusNode, parent, (n): n is Element => {
						if ("fixupCaret" in n) {
							updated = (n as IFixCaret).fixupCaret(this, oldFocus, oldFocusOffs);
							return true;
						}
					});
				} else {
					DOMSH.findFlatParentElt(focusNode, parent, (n): n is Element => {
						if ("fixupSelection" in n) {
							updated = (n as IFixSelection).fixupSelection(this, 3, oldFocus, oldFocusOffs) || updated;
							return true;
						}
					});
				}
			} else {
				DOMSH.findFlatParentElt(focusNode, parent, (n): n is Element => {
					if ("fixupSelection" in n) {
						updated = (n as IFixSelection).fixupSelection(this, 2, oldFocus, oldFocusOffs) || updated;
						return true;
					}
				});
				DOMSH.findFlatParentElt(anchorNode, parent, (n): n is Element => {
					if ("fixupSelection" in n) {
						updated = (n as IFixSelection).fixupSelection(this, 1, oldFocus, oldFocusOffs) || updated;
						return true;
					}
				});
			}
			this._around = around;
			//console.log("After fixupSel::", this._sel.type, this._sel.focusNode, this._sel.focusOffset, this._sel.anchorNode, this._sel.anchorOffset);
			return updated;
		} finally {
			this._selFixing = false;
		}
	}

	//protected _selFixed = false; optimisation abandonnée car cas rares de sel non fixed à l'exécution d'actions.

	onSelChange() {
		if (this._updtFromUs > 0) {
			this._updtFromUs--;
		} else {
			this._around = null;
		}
		if (this._sel.focusNode) {
			const rg = this._sel.getRangeAt(0);
			if (!rg.collapsed) {
				const newTags = new Set<TxtElement>();
				const tw = document.createTreeWalker(rg.commonAncestorContainer, NodeFilter.SHOW_ELEMENT, (n: Node) => {
					return n instanceof TxtBlock || n instanceof InlElement ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
				});
				let n: TxtElement;
				while ((n = tw.nextNode() as TxtElement)) {
					const offset = DOM.computeOffset(n);
					const posStart = rg.comparePoint(n.parentNode, offset);
					if (posStart < 1) {
						const posEnd = rg.comparePoint(n.parentNode, offset + 1);
						if (posEnd > -1) {
							tagsInSel?.delete(n);
							if (posStart === 0) DOM.setAttr(n, "in-sel", posEnd === 0 ? "start end" : "start");
							else DOM.setAttr(n, "in-sel", posEnd === 0 ? "end" : null);
							newTags.add(n);
						}
					}
				}
				tagsInSel = newTags.size > 0 ? newTags : null;
			}
			this.memSel(rg);
			this.ensureSelVisible();
		}
		//console.log("onSelChangeTxtSel::", this._sel.type, this._sel.focusNode, this._sel.focusOffset, this._sel.anchorNode, this._sel.anchorOffset);
	}

	protected memSel(rg: Range) {
		this._oldSel.setStart(rg.startContainer, rg.startOffset);
		this._oldSel.setEnd(rg.endContainer, rg.endOffset);
		this._oldFocusIsStart = this._sel.focusNode === rg.startContainer && this._sel.focusOffset === rg.startOffset;
	}

	onFocus(ev: FocusEvent) {
		if (ev.target === this._root) {
			this._lastObject = null;
			if (this.type === 'None') {
				//pas de sel actuellement, on se place au début
				this.setCaretAtStart(findTxtEltFirstChild(this._root));
				ev.preventDefault();
			}
		} else {
			this._lastObject = ev.target as HTMLElement;
			//if (this._lastObject.tabIndex >= 0) {
			//on est un Object
			this._sel.empty();
			//}
		}
	}

	onBlur(ev: FocusEvent) {
	}
}

/**
 * Cache pour le redraw des actions.
 * Met en cache des données pour éviter des recalculs internes et du layout de la page lors du redraw de chaque action.
 * En particulier l'état de la selection doit être cachée pouré viter le relayout.
 */
export class TxtSelMgrFrozen {
	type: 'None' | 'Caret' | 'Range' | 'Object';
	range: Range;
	focusNode: HTMLElement | Text;
	anchorNode: HTMLElement | Text;
	commonAncestor: HTMLElement | Text;
	tableLayout: TableLayout;
	selMgr: TxtSelMgr;

	constructor(selMgr: TxtSelMgr) {
		this.selMgr = selMgr;
		this.type = selMgr.type;
		this.focusNode = selMgr.focusNode;
		this.anchorNode = selMgr.anchorNode;
		this.commonAncestor = selMgr.commonAncestor;
		this.range = this.type === 'Range' ? selMgr.range : null;
		this.tableLayout = selMgr.tableLayout;
	}

	restoreSel(): TxtSelMgr {
		return this.selMgr.restoreSel();
	}
}

/**
 * On fake l'implémentation de TxtSelMgr : si une méthode de TxtSelMgr est appelée
 * pendant un cycle de refresh, on aura une exception.
 */
export interface TxtSelMgrFrozen extends TxtSelMgr {}

/**
 *
 */
class TxtStackBar extends HTMLElement {

	ctn: HTMLElement;

	protected lastTxtRoot: TxtRoot;
	protected lastCb: number;
	protected redrawCb: (deadline: RequestIdleDeadline) => void;

	constructor() {
		super();
		this.id = "txtStackBar";
		this.redrawCb = this._redrawCb.bind(this);
	}

	connectedCallback() {
		if (!this.shadowRoot) {
			const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
			const reg = REG.findReg(this);
			reg.installSkin("txt-stackbar", sr);
			this.ctn = sr.appendChild(document.createElement("div"));
		}
	}

	refreshBar(wedMgr: WedMgr, txtRoot?: TxtRoot) {
		if (txtRoot === undefined && this.lastTxtRoot === undefined) return;
		if (this.hidden) return;
		this.lastTxtRoot = txtRoot;
		if (!this.lastCb) this.lastCb = window.requestIdleCallback(this.redrawCb);
	}

	_redrawCb(deadline: RequestIdleDeadline) {
		this.lastCb = 0;
		if (!this.isConnected) return;
		let entry = this.ctn.lastElementChild as TxtStackEntry;
		if (this.lastTxtRoot) {
			let anc = findTxtEltParent(this.lastTxtRoot.selMgrAsIs.commonAncestor, true);
			if (anc instanceof TxtStr) anc = findTxtEltParent(anc);
			while (anc) {
				if (!entry) entry = this.ctn.insertBefore(new TxtStackEntry(), this.ctn.firstChild);
				entry.setNode(anc);
				anc = findTxtEltParent(anc);
				entry = entry.previousElementSibling as TxtStackEntry;
			}
		}
		while (entry) {
			entry.setNode(null);
			entry = entry.previousElementSibling as TxtStackEntry;
		}
	}

}

REG.reg.registerSkin("txt-stackbar", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 1.5em;
		min-width: 0;
		background-color: var(--bgcolor);
		font-size: var(--label-size);
		user-select: none;
	}

	txt-stackentry {
		cursor: pointer;
		color: var(--fade-color);
	}

	.selIn {
		display: inline-block;
		background: url('/@skin@/edit/wed/txt/selInTag.svg') no-repeat center/contain;
		vertical-align: middle;
		width: 1.2em;
		height: 1.2em;
		width: 1lh;
		height: 1lh;
		padding: 0 0.3em;
	}
`);
customElements.define("txt-stackbar", TxtStackBar);


class TxtStackEntry extends HTMLElement {

	txtElt: TxtElement;

	constructor() {
		super();
		this.onclick = this.onClick;
		this.onpointerdown = this.onPointerdown;
	}

	setNode(elt: TxtElement) {
		if (this.txtElt === elt) return;
		this.txtElt = elt;
		if (!elt || !elt.model.nodeLabel || elt instanceof TxtRow || elt instanceof TxtCell) {
			DOM.setHidden(this, true);
		} else if (elt instanceof InlWrap) {
			DOM.setHidden(this, false);
			this.replaceChildren(
				" > " + (elt.model.nodeLabel || elt.model.nodeName),
				<span class="selIn" title="Sélectionne l'intérieur de la balise"/>
			);
		} else {
			DOM.setHidden(this, false);
			this.textContent = " > " + (elt.model.nodeLabel || elt.model.nodeName);
		}
	}

	onClick(ev: MouseEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		if (!this.txtElt || !this.txtElt.isConnected) return;
		if ((ev.target as HTMLElement).localName === "span") {
			this.txtElt.txtRoot.selMgr.select(this.txtElt);
		} else {
			this.txtElt.txtRoot.selMgr.selectAround(this.txtElt);
		}
	}

	onPointerdown(ev: MouseEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
	}
}

customElements.define("txt-stackentry", TxtStackEntry);

//************************** Table *********************
export class TableLayout {
	txtTable: TxtTable;

	enabled: boolean;
	locked: boolean;

	anchorCell: TxtCell;
	focusCell: TxtCell;

	tblEditNode: TxtTblEditor;

	cellSelector: HTMLSpanElement;

	yRows: number[];
	xCols: number[];

	protected idleCb: (deadline: RequestIdleDeadline) => void;

	constructor(table: TxtTable) {
		this.txtTable = table;
		this.idleCb = this._idle.bind(this);
	}

	drawTableLayout(focusCell?: TxtCell, anchorCell?: TxtCell) {
		if (this.enabled) {
			this.tblEditNode.ensureFocused();
			if (focusCell) {
				this.focusCell = focusCell;
				this.anchorCell = anchorCell || focusCell;
				this.refreshCellSel(this.txtTable.getBoundingClientRect());
			}
			return;
		}
		this.enabled = true;
		if (!this.tblEditNode) {
			this.tblEditNode = document.createElement("txt-tbl-editor") as TxtTblEditor;
			this.tblEditNode.tabIndex = 0;
			this.tblEditNode.tableLayout = this;
			this.txtTable.style.position = "relative";
			this.txtTable.shadowRoot.appendChild(this.tblEditNode);
		}
		this.txtTable.table.style.minWidth = "max-content";
		this.txtTable.style.padding = "0 1em";
		const txtRoot = this.txtTable.txtRoot;
		let focus = focusCell || DOM.findParent(txtRoot.selMgr.focusNode, txtRoot, TXTTABLE.IS_TxtCell);
		if (focus && focus.parentNode.parentNode.parentNode !== this.txtTable) focus = null;
		let anchor = focusCell ? anchorCell : txtRoot.selMgr.type === 'Range' ? DOM.findParent(txtRoot.selMgr.anchorNode, txtRoot, TXTTABLE.IS_TxtCell) : null;
		if (anchor && anchor.parentNode.parentNode.parentNode !== this.txtTable) anchor = null;
		if (!focus && !anchor) focus = DOM.findNext(this.txtTable, this.txtTable, TXTTABLE.IS_TxtCell);
		this.focusCell = focus || anchor;
		this.anchorCell = anchor || focus;
		this.adjustTableLayout(true);
		this.txtTable.txtRoot.selMgrAsIs.tableLayout = this;
		this.tblEditNode.show();
		window.requestIdleCallback(this.idleCb);

	}

	/** */
	onBlur(ev: FocusEvent) {
		if (!this.enabled || this.locked) return;
		if (ev.relatedTarget) {
			if (DOMSH.isLogicalFlatAncestor(this.txtTable.focusBar, ev.relatedTarget as Node)) {
				//Focus sur une action de notre tableLayout
				//on ne masque pas, on reporte la fermeture du tableLayout sur le blur de cette action.
				const blur = (ev: FocusEvent) => {
					ev.target.removeEventListener('blur', blur);
					this.onBlur(ev);
				};
				ev.relatedTarget.addEventListener('blur', blur);
				return;
			}
			if (DOMSH.isLogicalFlatAncestor(this.tblEditNode, ev.relatedTarget as Node)) {
				//Menu contextuel...
				const menu = POPUP.findPopupableParent(ev.relatedTarget as Node);
				if (menu) {
					menu.onNextClose().then(() => {
						if (!DOMSH.findDeepActiveElement(this.tblEditNode.getRootNode({composed: true}) as ShadowRoot)) {
							this.closeTableLayout();
						}
					});
					return;
				}
			}
		}
		this.txtTable.txtRoot.selMgrAsIs.tableLayout = null;
		this.enabled = false;
		this.txtTable.table.style.minWidth = null;
		this.txtTable.style.padding = null;
		// if (DOMSH.findDocumentOrShadowRoot(this.tblEditNode).activeElement === this.tblEditNode) {
		// 	this.txtTable.txtRoot.focus();
		// }
		this.tblEditNode.hide();
	}

	closeTableLayout() {
		//if (!this.enabled) return; NON : avec le btn en toolbar, le blur a déjà eu lieu, donc déjà disabled.
		this.txtTable.txtRoot.selMgr.select(this.getFirstCellSelected() as any as TxtElement);
	}

	toggle() {
		if (this.enabled) {
			this.closeTableLayout();
		} else {
			this.drawTableLayout();
		}
	}

	/** Réévalue/repositionne ce tableLayout en état actif. */
	adjustTableLayout(force?: boolean) {
		if (!this.enabled) return;
		let col = DOM.findFirstChild(this.txtTable.table, TXTTABLE.IS_TxtCol);
		if (!col) return;
		let row = DOM.findNextSibling(col, TXTTABLE.IS_TxtRow);
		if (!row) return;

		const dir = window.getComputedStyle(this.txtTable).direction as "ltr" | "rtl" || 'ltr';
		const txtTableRect = this.txtTable.getBoundingClientRect();
		const tableRect = this.txtTable.table.getBoundingClientRect();
		const tableLeft = tableRect.left - txtTableRect.left;
		const yRows: number[] = [];
		while (row) {
			yRows.push(row.offsetTop);
			row = DOM.findNextSibling(row, TXTTABLE.IS_TxtRow);
		}
		row = DOM.findLastChild(this.txtTable.table, TXTTABLE.IS_TxtRow);
		yRows.push(row.offsetTop + row.clientHeight);

		const wCols: number[] = [];
		let wColTotal = 0;
		while (col) {
			const w = (col as any /*XXX CSStypedOM */).computedStyleMap().get('width').value as number; //XXX computedStyleMap couteux
			wColTotal += w;
			wCols.push(w);
			col = DOM.findNextSibling(col, TXTTABLE.IS_TxtCol);
		}

		const rowW = this.txtTable.table.clientWidth;
		let xCol = dir === "ltr" ? tableLeft + (tableRect.width - rowW) / 2 : tableLeft + rowW - (tableRect.width - rowW) / 2;
		const xDelta = (rowW - wColTotal) / wCols.length;
		const xCols: number[] = [Math.round(xCol)];
		if (dir === "ltr") {
			for (const wC of wCols) {
				xCol += wC + xDelta;
				xCols.push(Math.round(xCol));
			}
		} else {
			for (const wC of wCols) {
				xCol -= wC + xDelta;
				xCols.push(Math.round(xCol));
			}
		}

		//diffs yRows et xCols avec les précedents, si égal, on sort.
		if (!force && this.yRows && arrayEquals(this.yRows, yRows) && arrayEquals(this.xCols, xCols)) return;
		this.yRows = yRows;
		this.xCols = xCols;

		//Etend la zone de fond au delà du 100% du container si nécessaire
		(this.tblEditNode as any /*XXX CSStypedOM */).attributeStyleMap.set('width', (CSS as any /*XXX CSStypedOM */).px(tableRect.width + (tableRect.left - txtTableRect.left) * 2));

		let btn;
		if (WEDLET.isWritableWedlet(this.txtTable)) {
			const xPosLeft = (CSS as any /*XXX CSStypedOM */).px(tableLeft);
			const xPosRight = (CSS as any /*XXX CSStypedOM */).px(tableLeft + tableRect.width);
			const xPosStart = dir === 'ltr' ? xPosLeft : xPosRight;
			const xPosEnd = dir === 'ltr' ? xPosRight : xPosLeft;
			for (const y of yRows) {
				btn = this.getBtn(btn, 'insRow', '+', "Insérer une ligne");
				const st = (btn as any /*XXX CSStypedOM */).attributeStyleMap;
				st.set('top', (CSS as any /*XXX CSStypedOM */).px(y));
				st.set('left', xPosStart);
			}
			const yPos = (CSS as any /*XXX CSStypedOM */).px(yRows[0]);
			for (const x of xCols) {
				btn = this.getBtn(btn, 'insCol', '+', "Insérer une colonne");
				const st = (btn as any /*XXX CSStypedOM */).attributeStyleMap;
				st.set('left', (CSS as any /*XXX CSStypedOM */).px(x));
				st.set('top', yPos);
			}
			if (yRows.length > 2) for (let i = 1; i < yRows.length; i++) {
				btn = this.getBtn(btn, 'delRow', '-', "Supprimer cette ligne");
				const st = (btn as any /*XXX CSStypedOM */).attributeStyleMap;
				st.set('top', (CSS as any /*XXX CSStypedOM */).px((yRows[i - 1] + yRows[i]) / 2));
				st.set('left', xPosEnd);
			}
			if (xCols.length > 2) for (let i = 1; i < xCols.length; i++) {
				btn = this.getBtn(btn, 'delCol', '-', "Supprimer cette colonne");
				const st = (btn as any /*XXX CSStypedOM */).attributeStyleMap;
				st.set('left', (CSS as any /*XXX CSStypedOM */).px((xCols[i - 1] + xCols[i]) / 2));
				st.set('top', yPos);
			}
		}
		this.buildCellSelector(btn);
		this.refreshCellSel(txtTableRect);
	}

	/** Controle de la sel en cas de modifs conurrentes. */
	checkSel() {
		if (!this.anchorCell.isConnected) this.anchorCell = this.focusCell;
		if (!this.focusCell.isConnected) {
			if (this.focusCell === this.anchorCell) {
				//Sel supprimée : cas très rares, on sel arbitrairement la 1ère cell du tableau
				this.focusCell = this.anchorCell = DOM.findNext(this.txtTable.table, this.txtTable.table, TXTTABLE.IS_TxtCell);
			} else {
				this.focusCell = this.anchorCell;
			}
		}
	}

	/** Redessine la zone sélectionnée. */
	refreshCellSel(txtTableRect: DOMRect | ClientRect) {
		this.checkSel();
		const st = (this.cellSelector as any /*XXX CSStypedOM */).attributeStyleMap;
		const c1Rect = this.anchorCell.getBoundingClientRect();
		let x1, x2, y1, y2: number;
		if (this.anchorCell !== this.focusCell) {
			const c2Rect = this.focusCell.getBoundingClientRect();
			x1 = Math.min(c1Rect.left, c2Rect.left);
			x2 = Math.max(c1Rect.right, c2Rect.right);
			y1 = Math.min(c1Rect.top, c2Rect.top);
			y2 = Math.max(c1Rect.bottom, c2Rect.bottom);
		} else {
			x1 = c1Rect.left;
			x2 = c1Rect.right;
			y1 = c1Rect.top;
			y2 = c1Rect.bottom;
		}
		st.set('top', (CSS as any /*XXX CSStypedOM */).px(y1 - txtTableRect.top));
		st.set('left', (CSS as any /*XXX CSStypedOM */).px(x1 - txtTableRect.left));
		st.set('width', (CSS as any /*XXX CSStypedOM */).px(x2 - x1));
		st.set('height', (CSS as any /*XXX CSStypedOM */).px(y2 - y1));
		//
		const commonBars = (this.txtTable.wedMgr.wedEditor as IWedEditorCommonBar).commonBar;
		if (commonBars) commonBars.planChangeFocusBar(this.txtTable.focusBar);
	}

	getFirstCellSelected(): TxtCell {
		return this.focusCell;
	}

	moveCellSel(target: TxtCell, extendSel?: boolean) {
		if (!target) return;
		if (extendSel) {
			this.focusCell = target;
		} else {
			this.anchorCell = this.focusCell = target;
		}
		this.refreshCellSel(this.txtTable.getBoundingClientRect());
		target.scrollIntoView({behavior: "smooth", block: "nearest", inline: "nearest"});
	}

	get logicTable(): LogicTable {return new TXTTABLE.LogicTable(this.txtTable)}


	onCopy(ev?: Event) {
		this.txtTable.wedMgr.writeNodesToClipboard([this.txtTable.wedAnchor], [{tableRect: this.logicTable.getSelRect()}]);
	}

	onCopyXml() {
		this.txtTable.wedMgr.writeNodesToClipboardAsXml([this.txtTable.wedAnchor], [{tableRect: this.logicTable.getSelRect()}]);
	}

	onCut() {
		if (CLIPBOARD.hasCustomType) {
			//Si api async clipboard dispo, on doit toujours passer par elle (sinon type application/xml pas visible au paste via menu).
			const data = new DataTransfer();
			this.cutOrDelete(true, data);
			const formats: Dict<Blob> = {};
			if (data.types.indexOf("text/plain")) formats["text/plain"] = new Blob([data.getData('text/plain')], {type: "text/plain"})
			if (data.types.indexOf("text/html")) formats["text/html"] = new Blob([data.getData('text/html')], {type: "text/html"})
			if (data.types.indexOf("application/xml")) formats["web application/xml"] = new Blob([data.getData('application/xml')], {type: "application/xml"})
			CLIPBOARD.writeInClipboard(() => navigator.clipboard.write([new ClipboardItem(formats)]), this.txtTable);
		} else {
			this.cutOrDelete(true);
		}
	}

	cutOrDelete(cut: boolean, data?: DataTransfer) {
		let logicTable = this.logicTable;
		let selRect = logicTable.getSelRect();

		const execute = (action: 'delRows' | 'delCols' | 'delTable', data?: DataTransfer) => {
			const batch = this.txtTable.wedMgr.docHolder.newBatch();
			switch (action) {
			case 'delRows':
				batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
				if (cut) this.txtTable.wedMgr.writeNodesToClipboard([this.txtTable.wedAnchor], [{tableRect: {startRow: selRect.startRow, startCol: 0, endRow: selRect.endRow, endCol: this.txtTable.countCols()}}], data);
				logicTable.deleteRows(batch, selRect.startRow, selRect.endRow - selRect.startRow);
				batch.doBatch();
				break;
			case 'delCols':
				batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
				if (cut) this.txtTable.wedMgr.writeNodesToClipboard([this.txtTable.wedAnchor], [{tableRect: {startRow: 0, startCol: selRect.startCol, endRow: logicTable.logicRows.length, endCol: selRect.endCol}}], data);
				logicTable.deleteCols(batch, selRect.startCol, selRect.endCol - selRect.startCol);
				batch.doBatch();
				break;
			case 'delTable':
				if (cut) this.txtTable.wedMgr.writeNodesToClipboard([this.txtTable.wedAnchor], [], data);
				batch.deleteSequence(this.txtTable.wedAnchor, 1);
				batch.doBatch();
			}
		};
		const allCols = selRect.startCol === 0 && selRect.endCol === this.txtTable.countCols();
		if (selRect.startRow === 0 && selRect.endRow === logicTable.logicRows.length) {
			//cut colonnes or table
			execute(allCols ? 'delTable' : 'delCols', data);
		} else if (allCols) {
			//cut rows
			execute('delRows', data);
		} else {
			const actions = [];
			actions.push(new Action<TableLayout>('delRows').setLabel("ligne(s)"));
			actions.push(new Action<TableLayout>('delCols').setLabel("colonne(s)"));
			actions.push(new Action<TableLayout>('delTable').setLabel("tableau entier"));
			this.locked = true;
			try {
				const popup = new PopupActions<TableLayout>().initialize({
					actionContext: this,
					actions,
					headTitle: cut ? "Couper..." : "Supprimer...",
					restoreFocus: this.tblEditNode
				});
				popup.addEventListener('c-close', (ev: IPopupCloseEvent) => {
					if (ev.detail.returnValue) {
						logicTable = this.logicTable; //refresh en cas d'édition concurrente
						selRect = logicTable.getSelRect();
						execute(ev.detail.returnValue.getId() as any);
					}
				});
				popup.show(this.txtTable.table, this.txtTable, this.txtTable);
			} finally {
				this.locked = false;
			}
		}
	}


	buildImportContext(): OSkTableImportContext {
		if (!this.focusCell || this.focusCell !== this.anchorCell) {
			POPUP.showNotifInfo("Sélectionnez une seule cellule pour le point de départ du collage.", this.txtTable);
			return null;
		}
		return {sel: {start: XA.append(this.txtTable.wedAnchor, 0)}, cellInsertPoint: this.focusCell.wedAnchor};
	}

	protected onClickBtn(this: ITableLayoutBtn, ev: MouseEvent) {
		const tableEd = DOMSH.findHost(this) as TxtTblEditor;
		const txtTable = tableEd.tableLayout.txtTable;
		ev.stopImmediatePropagation();
		if (this.classList.contains('insCol')) {
			const colModels = txtTable.model.colModels;
			const batch = txtTable.wedMgr.docHolder.newBatch();
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			tableEd.tableLayout.logicTable.insertCols(batch, this.logicalOffset, colModels[0]);
			batch.doBatch();
		} else if (this.classList.contains('insRow')) {
			const rowModels = txtTable.model.rowModels;
			const batch = txtTable.wedMgr.docHolder.newBatch();
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			tableEd.tableLayout.logicTable.insertRows(batch, this.logicalOffset, rowModels[0]);
			batch.doBatch();
		} else if (this.classList.contains('delRow')) {
			const batch = txtTable.wedMgr.docHolder.newBatch();
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			tableEd.tableLayout.logicTable.deleteRows(batch, this.logicalOffset, 1);
			batch.doBatch();
		} else if (this.classList.contains('delCol')) {
			const batch = txtTable.wedMgr.docHolder.newBatch();
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			tableEd.tableLayout.logicTable.deleteCols(batch, this.logicalOffset, 1);
			batch.doBatch();
		}
	}

	protected getBtn(prev: ITableLayoutBtn, cls: string, label: string, title: string): ITableLayoutBtn {
		let curr = prev ? prev.nextElementSibling : DOM.findFirstChild(this.tblEditNode.shadowRoot, (n: Node): n is HTMLElement => n instanceof HTMLElement && n.classList.contains(cls));
		if (prev && !prev.classList.contains(cls)) {
			//cleanup des btns en trop du type précédent.
			while (curr && curr.localName === 'button' && !curr.classList.contains(cls)) {
				prev = curr as ITableLayoutBtn;
				curr = curr.nextElementSibling;
				prev.remove();
			}
			prev = null;
		}
		if (curr instanceof HTMLElement && curr.classList.contains(cls)) return curr;
		const btn = document.createElement("button") as ITableLayoutBtn;
		btn.logicalOffset = prev ? prev.logicalOffset + 1 : 0;
		btn.tabIndex = -1;
		btn.classList.add(cls);
		btn.appendChild(document.createElement('span')).textContent = label;
		btn.title = title;
		btn.onclick = this.onClickBtn;
		btn.onpointerdown = (ev: MouseEvent) => {ev.stopImmediatePropagation();}; //bloque pour les div autour de la table masquant le tableLayout.
		this.tblEditNode.shadowRoot.insertBefore(btn, curr);
		return btn;
	}

	protected buildCellSelector(prev: Element): HTMLElement {
		prev = prev ? prev.nextElementSibling : null;
		while (prev && prev.localName === 'button') {
			//cleanup des buttons en trop.
			const next = prev.nextElementSibling;
			prev.remove();
			prev = next;
		}
		if (!this.cellSelector) {
			this.cellSelector = document.createElement("div");
			this.cellSelector.classList.add('cellSelect');
			this.tblEditNode.shadowRoot.appendChild(this.cellSelector);
		}
		return this.cellSelector;
	}

	protected _idle(deadline: RequestIdleDeadline) {
		if (!this.enabled || !this.tblEditNode.isConnected) return;
		if (deadline.timeRemaining() > 20) this.adjustTableLayout();
		window.requestIdleCallback(this.idleCb);
	}
}


export function onTxtTblEdBlur(this: TxtTblEditor, ev: FocusEvent) {
	this.tableLayout.onBlur(ev);
}

interface ITableLayoutBtn extends HTMLElement {
	logicalOffset?: number
}

class TxtTblEditor extends BaseElement implements IWedFocusBarPointer {
	tableLayout: TableLayout;

	_onTblEdPointerCancel: (this: TxtTblEditor, ev: PointerEvent) => void;

	constructor() {
		super();
		this.addEventListener("blur", onTxtTblEdBlur);
		this.addEventListener("pointerdown", onTblEdPointerDown);
		this.addEventListener("keydown", onTblEdKeyDown);
		this._onTblEdPointerCancel = onTblEdPointerCancel.bind(this);
	}

	_initialize(init: OSkinableInit) {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
	}

	show() {
		this.hidden = false;
		this.focus();
	}

	hide() {
		this.hidden = true;
	}

	ensureFocused() {
		if (DOMSH.findDocumentOrShadowRoot(this).activeElement !== this) {
			//on récupère le focus au besoin : le focus peut être dans la focusBar.
			this.tableLayout.locked = true; //pour que le suivi du blur (cf TableLayout.onBlur()) ne provoque pas la désactivation du tableLayout.
			try {
				this.focus();
			} finally {
				this.tableLayout.locked = false;
			}
		}
	}

	focus() {
		super.focus();
		if (!this.tableLayout.enabled) this.tableLayout.drawTableLayout(this.tableLayout.focusCell, this.tableLayout.anchorCell);
	}

	get focusBar(): BarActions<IWedletActionCtx> {return this.tableLayout.txtTable.focusBar}
}

window.customElements.define("txt-tbl-editor", TxtTblEditor);

REG.reg.registerSkin('txt-tbl-editor', 1, /* language=CSS */ `
	:host {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: .8;
		box-sizing: border-box;
	}

	:host(:focus) {
		outline: none;
	}

	:host([hidden]) {
		display: none;
	}

	button {
		-webkit-appearance: none;
		position: absolute;
		display: block;
		width: 1em;
		height: 1em;
		border: none;
		border-radius: .5em;
		padding: 0;
		font-weight: bold;
		color: var(--insbtn-color);
		background-color: var(--insbtn-bg);
	}

	button:active {
		filter: brightness(1.1);
	}

	button:hover {
		background-color: var(--insbtn-hover-bg);
	}

	.insCol,
	.delCol {
		margin-top: -1.2em;
		margin-left: -.5em;
	}

	.insRow {
		margin-top: -.5em;
		margin-left: -1.2em;
	}

	.delRow {
		margin-top: -.5em;
		margin-left: -.5em;
	}

	.delCol > span,
	.delRow > span {
		display: block;
		margin-top: -.2em;
	}


	.cellSelect {
		position: absolute;
		outline: var(--edit-box-focus);
		background-color: var(--edit-select-bgcolor);
	}
`);

function onTblEdPointerDown(this: TxtTblEditor, ev: PointerEvent) {
	ev.stopImmediatePropagation();
	ev.preventDefault(); //Obligatoire sinon un evt selectionchange est propagé et reset la commonBar.
	if (ev.button !== 0) return;
	this.ensureFocused();
	const rect = this.getBoundingClientRect();
	if (ev.x < rect.left + this.tableLayout.xCols[0] || ev.x > rect.left + this.tableLayout.xCols[this.tableLayout.xCols.length - 1]) {
		//hors tableau (en marge à droite ou à gauche), on sort
		this.tableLayout.toggle();
	} else {
		const cell = cellFromPoint(this.tableLayout.txtTable, ev.x, ev.y);
		if (cell) {
			if (!ev.shiftKey) this.tableLayout.anchorCell = cell;
			this.tableLayout.focusCell = cell;
			this.tableLayout.refreshCellSel(this.tableLayout.txtTable.getBoundingClientRect());
			this.addEventListener("pointermove", onTblEdPointerMove);
			document.addEventListener("pointerup", this._onTblEdPointerCancel);
			document.addEventListener("pointercancel", this._onTblEdPointerCancel);
		}
	}
}

function cellFromPoint(txtTable: TxtTable, x: number, y: number): TxtCell | null {
	return document.createTreeWalker(txtTable.table, NodeFilter.SHOW_ELEMENT, {
		acceptNode(node: HTMLElement): number {
			if (node instanceof TxtCell) {
				if (GFX.isPointIn(node.getBoundingClientRect(), x, y)) return NodeFilter.FILTER_ACCEPT;
				return NodeFilter.FILTER_REJECT;
			}
			return NodeFilter.FILTER_SKIP;
		}
	}).nextNode() as TxtCell;
}

function onTblEdKeyDown(this: TxtTblEditor, ev: KeyboardEvent) {
	//console.log("onTblEdKeyDown:::", ev.key);
	switch (ev.key) {
	case 'ArrowLeft':
		this.tableLayout.moveCellSel(DOM.findPreviousSibling(this.tableLayout.focusCell, TXTTABLE.IS_TxtCell), ev.shiftKey);
		break;
	case 'ArrowRight':
		this.tableLayout.moveCellSel(DOM.findNextSibling(this.tableLayout.focusCell, TXTTABLE.IS_TxtCell), ev.shiftKey);
		break;
	case 'ArrowUp': {
		const logicTable = this.tableLayout.logicTable;
		const {offsRow, offsCol} = logicTable.getLogicOffsets(this.tableLayout.focusCell);
		this.tableLayout.moveCellSel(logicTable.getTxtCellAt(offsRow - 1, offsCol), ev.shiftKey);
		break;
	}
	case 'ArrowDown': {
		const logicTable = this.tableLayout.logicTable;
		const {offsRow, offsCol} = logicTable.getLogicOffsets(this.tableLayout.focusCell);
		this.tableLayout.moveCellSel(logicTable.getTxtCellAt(offsRow + 1, offsCol), ev.shiftKey);
		break;
	}
	case 'Insert':
	case 'Escape':
		this.tableLayout.toggle();
		break;
	case 'Delete':
	case 'Backspace':
		this.tableLayout.cutOrDelete(false);
		break;
	default:
		return; //autre touche, on ne stoppe pas l'event.
	}
	ev.stopImmediatePropagation();
	ev.preventDefault();
}

/** Utilisé pour drag&drop sel du TableLayout. */
function onTblEdPointerMove(this: TxtTblEditor, ev: PointerEvent) {
	const cell = cellFromPoint(this.tableLayout.txtTable, ev.x, ev.y);
	if (cell && this.tableLayout.focusCell !== cell) {
		this.tableLayout.focusCell = cell;
		this.tableLayout.refreshCellSel(this.tableLayout.txtTable.getBoundingClientRect());
		//FIXME ajouter des margin à scrollIntoView pour (cf https://github.com/stipsan/scroll-into-view-if-needed ?)
		cell.scrollIntoView({behavior: "smooth", block: "nearest", inline: "nearest"});
	}
}

export function onTxtTableFlexPointerDown(this: HTMLElement, ev: PointerEvent) {
	(DOMSH.findHost(this) as TxtTable).tableLayout.drawTableLayout();
	ev.stopImmediatePropagation();
	ev.preventDefault();
}

function onTblEdPointerCancel(this: TxtTblEditor, ev: PointerEvent) {
	document.removeEventListener("pointerup", this._onTblEdPointerCancel);
	document.removeEventListener("pointercancel", this._onTblEdPointerCancel);
	this.removeEventListener("pointermove", onTblEdPointerMove);
}

function arrayEquals(a1: any[], a2: any[]) {
	if (a1.length != a2.length) return false;
	for (let i = 0; i < a1.length; i++) if (a1[i] !== a2[i]) return false;
	return true;
}
